import {
  IconPlus,
  IconPresentationAnalytics,
  IconSparkles,
  IconSquareRoundedPlusFilled,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { DesktopBreadcrumb } from "../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopTopNav } from "../../components/app/desktopTopNav/DesktopTopNav";
import ContentHeader from "../../components/app/headers/ContentHeader";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import {
  VisualAbstractLibraryGridSkeleton,
  VISUAL_ABSTRACT_DESKTOP_LIBRARY_GRID_CLASS,
  VISUAL_ABSTRACT_LIBRARY_GRID_CLASS,
} from "../../components/skeleton/VisualAbstractLibrarySkeleton";
import { Button } from "../../components/ui/Button";
import { Logo } from "../../components/ui/Logo";
import { Tooltip } from "../../components/ui/Tooltip";
import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { renderDateRelative } from "../../domains/app/utils/renderDate";
import { gaEventVisualAbstractOpenedFromLibrary } from "../../tools/analytics/domains/article.analytics";
import {
  useApiInfiniteQuery,
  type Api,
  type InfiniteQueryResult,
} from "../../services/api";
import { useAcknowledgeVisualAbstracts } from "./unseenVisualAbstracts";
import { useRequestVisualAbstract } from "./useRequestVisualAbstract";
import {
  useFailedVisualAbstracts,
  useWatchedVisualAbstracts,
  type FailedVisualAbstract,
  type WatchedVisualAbstract,
} from "./visualAbstractWatchStore";

/**
 * The creation screen, told it was opened from here so a finished request can
 * step back to this entry rather than push a second library on the stack.
 */
const NEW_VISUAL_ABSTRACT_LOCATION = {
  pathname: "/visual-abstracts/new",
  state: { from: "library" },
} as const;

/**
 * The runs one device may have in flight. The library's creation button is
 * disabled at this count (Figma "D2.va-center.limit"). It counts this device's
 * watch list only: the api sets no limit of its own.
 */
const MAX_PENDING_VISUAL_ABSTRACTS = 2;

type LibraryEndpoint = Api["get"]["/webapp/private/visual-abstracts/library"];
type LibraryEntry = LibraryEndpoint["output"]["body"]["docs"][number];

/**
 * Every poster the user has had generated, in one place — reached from the
 * JuisciGPT drawer. The cards are built from the brief's header alone (no
 * figure ever leaves the server for this screen), so the grid is a list of
 * mini-posters rather than thumbnails of the real render: opening one is what
 * fetches the poster.
 */
export default function VisualAbstractLibraryPage(): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  // Arriving here IS the acknowledgement the red dot on JuisciGPT was asking
  // for, so the dot goes out on every device rather than on this one only.
  useAcknowledgeVisualAbstracts();
  // Runs this device launched and has not settled yet. They head the grid as
  // placeholders: the library route lists `ready` posters only, and a request
  // made a second ago would otherwise leave no trace here.
  const generating = useWatchedVisualAbstracts();
  const query = useApiInfiniteQuery(
    "/webapp/private/visual-abstracts/library",
    {
      query: { limit: 20 },
    }
  );

  // A run the library already lists is no longer a placeholder: between the
  // library refetch and the watcher's next pass, the same id would otherwise
  // show twice, once generating and once ready.
  const listedIds =
    query.state === "success"
      ? new Set(query.docs.map((entry) => entry.id))
      : new Set<string>();
  const pendingRuns = generating.filter((entry) => !listedIds.has(entry.id));

  if (isDesktopShell())
    return <DesktopLibrary query={query} pendingRuns={pendingRuns} />;

  return (
    <ScrollableLayout
      background="var(--color-ds-cream)"
      header={
        <ContentHeader
          onBack={() => {
            history.goBack();
          }}
          title={t("Visual abstracts")}
          transparent
          actions={
            <Link
              to={NEW_VISUAL_ABSTRACT_LOCATION}
              aria-label={t("New visual abstract")}
              className="flex text-ds-black"
            >
              <IconSquareRoundedPlusFilled className="size-6" />
            </Link>
          }
        />
      }
      headerClassName="bg-white/90 backdrop-blur-md"
    >
      <div className="mx-auto w-full max-w-6xl px-4 pt-4 pb-12">
        <div className="mb-4 hidden items-center justify-between gap-4 md:flex">
          <h1 className="m-0 font-dm-sans text-heading-lg font-bold text-ds-mint-950">
            {t("Visual abstracts")}
          </h1>
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              history.push(NEW_VISUAL_ABSTRACT_LOCATION);
            }}
          >
            {t("Create a visual abstract")}
          </Button>
        </div>
        {query.state === "pending" || query.state === "idle" ? (
          <VisualAbstractLibraryGridSkeleton count={6} shell="mobile" />
        ) : query.state === "error" ? (
          <LibraryMessage
            title={t("Visual abstracts")}
            body={t("This content is no longer available.")}
            action={null}
          />
        ) : query.docs.length === 0 && pendingRuns.length === 0 ? (
          <LibraryMessage
            title={t("No visual abstract yet")}
            body={t(
              "Turn an open-access article into a one-page poster you can share with colleagues."
            )}
            action={
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={() => {
                  history.push(NEW_VISUAL_ABSTRACT_LOCATION);
                }}
              >
                {t("Create a visual abstract")}
              </Button>
            }
          />
        ) : (
          <>
            {/* Two up from the narrowest phone, so a scroll shows a library
                rather than one poster at a time. The card is sized for that
                half-column (~180px on a 390pt screen), not scaled down from a
                full-width one. The grid class is the skeleton's, so the
                silhouette and the list cannot reflow differently. */}
            <ul className={VISUAL_ABSTRACT_LIBRARY_GRID_CLASS}>
              {pendingRuns.map((entry) => (
                <li key={entry.id}>
                  <GeneratingCard entry={entry} shell="mobile" />
                </li>
              ))}
              {query.docs.map((entry) => (
                <li key={entry.id}>
                  <LibraryCard entry={entry} />
                </li>
              ))}
            </ul>
            {query.hasNextPage ? (
              <div className="flex justify-center pt-6">
                <Button
                  variant="secondary"
                  size="md"
                  type="button"
                  onClick={() => {
                    query.fetchNextPage();
                  }}
                >
                  {t("See more")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </ScrollableLayout>
  );
}

/**
 * The card IS the poster's header band — the same chip, title, source line and
 * "generated by" lockup `VisualAbstractHeader` paints, in the artifact's own
 * brand colour. Deliberately a rebuild rather than a call into
 * `VisualAbstract`: that component needs the full view (hero, callouts, and
 * every figure inlined as base64), which is the payload this screen exists not
 * to fetch. What the two share is the contract they both read.
 */
function LibraryCard({ entry }: { entry: LibraryEntry }): React.ReactElement {
  return (
    <LibraryCardLink entry={entry}>
      <article
        className="flex h-full flex-col justify-between gap-4 rounded-2xl p-4  text-white"
        style={{ backgroundColor: entry.branding.primaryColor }}
      >
        <div className="min-w-0">
          <span className="inline-flex rounded-full bg-ds-mint-50 px-2.5 py-1 text-ui-caption font-extrabold tracking-wide text-ds-mint-800 uppercase">
            {entry.sourceTypeLabel}
          </span>
          {/* Clamped, not truncated at one line: the title is the only thing on
              the card that identifies the paper, and four lines of it at half
              width still reads as the poster's own headline. */}
          <h2 className="mt-3 mb-1.5 line-clamp-4 wrap-break-word font-dm-sans text-heading-xs font-bold">
            {entry.shortTitle}
          </h2>
          <p className="m-0 line-clamp-2 text-ui-caption text-ds-mint-50">
            {entry.journalAndYear}
          </p>
        </div>
        {/* The poster signs itself in the bottom-left; the card spends the half
            of that line the mark does not need on WHEN it was generated, which
            on a poster has no place but on a library entry is what tells two
            posters of the same paper apart. The mark is deliberately smaller
            here than on the poster — at poster scale it dominates a card whose
            job is to show a title. */}
        {/* `flex-wrap` rather than a breakpoint: the date sits beside the mark
            while the column is wide enough for both and drops under it when it
            is not, so the same card survives a 2-up phone grid and a 4-up
            desktop one without either being a special case. */}
        <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span className="text-ui-caption font-extrabold tracking-wide text-ds-mint-50 uppercase">
              {entry.language === "fr" ? "Généré par" : "Generated by"}
            </span>
            <LibraryBrandMark branding={entry.branding} />
          </div>
          <span className="shrink-0 text-ui-caption text-ds-mint-50">
            {renderDateRelative(entry.createdAt)}
          </span>
        </div>
      </article>
    </LibraryCardLink>
  );
}

/** A card opens its poster, and counts as an open from the library. */
function LibraryCardLink({
  entry,
  children,
}: {
  entry: LibraryEntry;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Link
      to={`/visual-abstract/${entry.id}/preview`}
      className="block h-full no-underline"
      onClick={() => {
        gaEventVisualAbstractOpenedFromLibrary({
          visualAbstractId: entry.id,
          roomId: entry.roomId,
          language: entry.language,
          family: entry.family,
          sourceType: entry.sourceType,
          generatorVersion: entry.generatorVersion,
        });
      }}
    >
      {children}
    </Link>
  );
}

/**
 * A run still in flight, in a card's slot. It names the paper from the watch
 * entry (the status route carries no brief), and it leaves on its own: the
 * watcher unwatches the run when it settles and refreshes this list, so the
 * poster takes the slot back.
 */
function GeneratingCard({
  entry,
  shell,
}: {
  entry: WatchedVisualAbstract;
  shell: "mobile" | "desktop";
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <article
      className={
        shell === "desktop"
          ? "flex h-full flex-col justify-between gap-4 rounded-2xl border border-dashed border-ds-mint-800/50 bg-ds-mint-50 p-5"
          : "flex h-full flex-col justify-between gap-4 rounded-2xl border border-dashed border-ds-mint-800/25 bg-ds-mint-50 p-4"
      }
    >
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-mint-800 px-2.5 py-1 text-ui-caption font-extrabold tracking-wide text-ds-mint-50 uppercase">
          <IconSparkles className="size-3" />
          {t("Generating")}
        </span>
        <h2
          className={
            shell === "desktop"
              ? "mt-3 mb-0 line-clamp-4 font-dm-sans text-publication-lg font-semibold wrap-break-word text-ds-mint-800"
              : "mt-3 mb-0 line-clamp-4 wrap-break-word font-dm-sans text-heading-xs font-bold text-ds-mint-800"
          }
        >
          {entry.articleTitle}
        </h2>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="h-2 w-full animate-pulse rounded-full bg-ds-mint-800/15" />
        <span className="h-2 w-2/3 animate-pulse rounded-full bg-ds-mint-800/15" />
        <span className="text-ui-caption font-medium text-ds-mint-800">
          {t("About a minute")}
        </span>
      </div>
    </article>
  );
}

function LibraryBrandMark({
  branding,
}: {
  branding: LibraryEntry["branding"];
}): React.ReactElement {
  switch (branding.source) {
    case "juisci":
      // `h-6` against the lockup's own `h-20` default: the mark is an
      // attribution on a card, not the card's subject. `cn` (tailwind-merge)
      // lets the height through — never recolour or redraw a lockup, only
      // resize it.
      return <Logo className="h-6" tone="white" variant="no-tagline" />;
    case "room":
      return (
        <img
          alt={branding.organisationName}
          className="max-h-6 max-w-20 rounded-lg bg-white object-contain p-1"
          src={branding.logoUrl}
        />
      );
    default:
      throw new Error(branding satisfies never);
  }
}

function LibraryMessage({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-ds-mint-50">
        <IconPresentationAnalytics
          className="size-6 text-ds-slate-700"
          stroke={1.25}
        />
      </div>
      <p className="m-0 font-dm-sans text-heading-xs font-semibold text-ds-mint-950">
        {title}
      </p>
      <p className="m-0 max-w-sm font-dm-sans text-ui-note font-medium text-ds-slate-500">
        {body}
      </p>
      {action === null ? null : <div className="pt-2">{action}</div>}
    </div>
  );
}

/**
 * The desktop shell's library (Figma "D2.va-center"): the page's own top bar
 * holds the breadcrumb and the creation button, and the grid is fixed-size
 * cards. A run this device watched to a failure stays as a retry card, after the
 * runs in flight, until a new run of the same paper and room starts or the
 * watch TTL collects it.
 */
function DesktopLibrary({
  query,
  pendingRuns,
}: {
  query: InfiniteQueryResult<LibraryEndpoint>;
  pendingRuns: WatchedVisualAbstract[];
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { gptNewChatPageLink } = usePageLinks(null);
  const failedRuns = useFailedVisualAbstracts();

  return (
    <div className="flex h-full flex-col overflow-hidden bg-ds-cream">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{ kind: "none" }}
            trail={[{ label: "JuisciGPT", to: gptNewChatPageLink() }]}
            current={t("Visual Abstracts")}
          />
        }
        trailing={{
          kind: "actions",
          content:
            pendingRuns.length >= MAX_PENDING_VISUAL_ABSTRACTS ? (
              <Tooltip
                side="below"
                content={{
                  kind: "label",
                  text: t("Two visual abstracts at a time"),
                }}
                trigger={{
                  kind: "box",
                  className: "inline-flex",
                  // A disabled button fires no pointer event in every browser:
                  // the wrapper takes the hover that opens the tooltip.
                  control: (descriptionId) => (
                    <Button
                      variant="primary"
                      size="sm"
                      type="button"
                      leftIcon={<IconPlus />}
                      disabled
                      aria-describedby={descriptionId}
                      className="pointer-events-none"
                    >
                      {t("New visual abstract")}
                    </Button>
                  ),
                }}
              />
            ) : (
              <Button
                variant="primary"
                size="sm"
                type="button"
                leftIcon={<IconPlus />}
                onClick={() => {
                  history.push(NEW_VISUAL_ABSTRACT_LOCATION);
                }}
              >
                {t("New visual abstract")}
              </Button>
            ),
        }}
        // The page's own colour shows through the bar, as in the design.
        surface="default"
        className="bg-transparent"
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-12 pt-8 pb-12">
        {query.state === "pending" || query.state === "idle" ? (
          <VisualAbstractLibraryGridSkeleton count={8} shell="desktop" />
        ) : query.state === "error" ? (
          <LibraryMessage
            title={t("Visual abstracts")}
            body={t("This content is no longer available.")}
            action={null}
          />
        ) : query.docs.length === 0 &&
          pendingRuns.length === 0 &&
          failedRuns.length === 0 ? (
          <LibraryMessage
            title={t("No visual abstract yet")}
            body={t(
              "Turn an open-access article into a one-page poster you can share with colleagues."
            )}
            action={
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  history.push(NEW_VISUAL_ABSTRACT_LOCATION);
                }}
              >
                {t("Create a visual abstract")}
              </Button>
            }
          />
        ) : (
          <>
            <ul className={VISUAL_ABSTRACT_DESKTOP_LIBRARY_GRID_CLASS}>
              {pendingRuns.map((entry) => (
                <li key={entry.id}>
                  <GeneratingCard entry={entry} shell="desktop" />
                </li>
              ))}
              {failedRuns.map((entry) => (
                <li key={entry.id}>
                  <FailedCard entry={entry} />
                </li>
              ))}
              {query.docs.map((entry) => (
                <li key={entry.id}>
                  <DesktopLibraryCard entry={entry} />
                </li>
              ))}
            </ul>
            {query.hasNextPage ? (
              <div className="flex justify-center pt-6">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => {
                    query.fetchNextPage();
                  }}
                >
                  {t("See more")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}

/**
 * The phone card's content at the desktop poster header's scale, with the date
 * on the "generated by" line and the mark under it. The mark is the phone
 * card's: a logo alone, never a name set in type, as on the poster.
 */
function DesktopLibraryCard({
  entry,
}: {
  entry: LibraryEntry;
}): React.ReactElement {
  return (
    <LibraryCardLink entry={entry}>
      <article
        className="flex h-full flex-col justify-between rounded-2xl p-5 text-white"
        style={{ backgroundColor: entry.branding.primaryColor }}
      >
        <div className="flex min-w-0 flex-col items-start">
          <span className="inline-flex rounded-full bg-ds-mint-50 px-2.5 py-1 text-ui-caption font-extrabold tracking-wide text-ds-mint-800 uppercase">
            {entry.sourceTypeLabel}
          </span>
          <h2 className="mt-3.5 mb-1.5 line-clamp-4 font-dm-sans text-publication-lg font-semibold wrap-break-word">
            {entry.shortTitle}
          </h2>
          <p className="m-0 line-clamp-2 text-footnote/4.5 text-ds-mint-50">
            {entry.journalAndYear}
          </p>
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <div className="flex w-full items-center justify-between gap-3 text-ds-mint-50">
            <span className="text-ui-caption font-extrabold tracking-wide uppercase">
              {entry.language === "fr" ? "Généré par" : "Generated by"}
            </span>
            <span className="shrink-0 text-footnote/4.5">
              {renderDateRelative(entry.createdAt)}
            </span>
          </div>
          <LibraryBrandMark branding={entry.branding} />
        </div>
      </article>
    </LibraryCardLink>
  );
}

/**
 * A run this device watched to a failure (see `recordFailedVisualAbstract`).
 * The retry asks for the same paper in the same room; the failure leaves the
 * list and the new run heads the grid as a generating card.
 */
function FailedCard({
  entry,
}: {
  entry: FailedVisualAbstract;
}): React.ReactElement {
  const { t } = useTranslation();
  const { isRequesting, request } = useRequestVisualAbstract();

  return (
    <article className="flex h-full flex-col justify-between rounded-2xl border border-dashed border-ds-strawberry-700 bg-ds-strawberry-50 p-5">
      <div className="flex min-w-0 flex-col items-start gap-3">
        <span className="inline-flex rounded-full bg-ds-strawberry-700 px-2.5 py-1 text-ui-caption font-extrabold tracking-wide text-white uppercase">
          {t("Not generated")}
        </span>
        <h2 className="m-0 line-clamp-4 font-dm-sans text-publication-lg font-semibold wrap-break-word text-ds-slate-950">
          {entry.articleTitle}
        </h2>
      </div>
      <div className="flex flex-col items-start gap-2.5">
        <p className="m-0 text-footnote/4.5 text-ds-slate-700">
          {t("Generation failed. Try again.")}
        </p>
        <Button
          variant="outline"
          size="sm"
          type="button"
          // A second click would send a second request for the same paper.
          disabled={isRequesting(entry.articleId)}
          onClick={() => {
            // A successful request clears this card: the new run heads the
            // grid as a generating card.
            void request({
              articleId: entry.articleId,
              articleTitle: entry.articleTitle,
              roomId: entry.roomId,
            });
          }}
        >
          {t("Try again")}
        </Button>
      </div>
    </article>
  );
}
